import moment from 'moment-timezone';
import Dotdotdot from 'react-dotdotdot';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Button, ButtonClose } from '@hivebrite/design-system/Button';
import { Card } from '@hivebrite/design-system/Card';
import { PillsList } from '@hivebrite/design-system/PillsList';
import { linkHoverEffect, zIndices } from '@hivebrite/design-system/Theme';
import { useIsMobileDevice } from '@hivebrite/react-hooks';

import AddToCalendar from 'hb-react/frontoffice/components/AddToCalendar';
import { EventWebinarUrl } from 'hb-react/shared/apps/Events/components/EventDetailsContainer/components/EventWebinarUrl';
import Attendees from 'hb-react/shared/components/DesignSystem/Attendees';
import EventDate from 'hb-react/shared/components/EventDate';
import TwoDigitsCalendar from 'hb-react/shared/icons/dynamic/TwoDigitsCalendar';
import IconExclamationMarkHollow from 'hb-react/shared/icons/ExclamationMarkHollow';
import { participantThresholdMet } from 'helpers/participantThresholdHelper';

const EventsMapCard = ({
  event,
  onClose,
  onFindMore,
  timezoneConfiguration,
}) => {
  const {
    id,
    start_date: startDate,
    title,
    locations,
    total_external_attendees: totalExternalAttendees,
    total_attendees: totalAttendees,
    attending_users: attendingUsers,
    attendees,
    webinar_url: webinarUrl,
    timezone,
    network_categories: networkCategories = [],
    participant_display_threshold: participantDisplayThreshold,
    total_visible_attendees: totalVisibleAttendees,
    total_visible_yes_responses: totalVisibleYesResponses,
    venue,
    display_add_to_calendar: displayAddToCalendar,
  } = event;

  const consolidatedAttendees = attendees || attendingUsers;

  const day = moment(startDate).tz(timezone).date();

  const location = Array.isArray(locations) ? locations[0] : locations;
  const hasLocation = location && location.lat && location.lng;

  const address = location?.address ?? '';

  return (
    <Wrapper data-testid="events-index-event-map-card">
      {webinarUrl && (
        <Card px={4} py={3} mb={3}>
          <Box justifyContent="center">
            <IconExclamationMarkHollow height={16} width={16} />
            <Box ml={3}>{__('This is an online event')}</Box>
          </Box>
        </Card>
      )}
      {!hasLocation && !webinarUrl && (
        <Card px={4} py={3} mb={3}>
          <Box justifyContent="center">
            <IconExclamationMarkHollow height={16} width={16} />
            <Box ml={3}>
              {__(
                'This event has no location and is not represented on the map',
              )}
            </Box>
          </Box>
        </Card>
      )}
      <StyledCard p={4}>
        <Box alignItems="center" color="primary">
          <StyledTwoDigitsCalendar
            day={day}
            width={42}
            height={42}
            fontWeight={1}
          />
          <EventTitle onClick={() => onFindMore(event)}>
            <ClampedLinkWorkaround clamp={2} title={title}>
              {title}
            </ClampedLinkWorkaround>
          </EventTitle>
          <ButtonClose
            css={css`
              align-self: flex-start;
            `}
            onClick={onClose}
          />
        </Box>
        <Box mt={4} mb={8}>
          <Box flexDirection="column" pr={6} flex="1">
            <ContentRow mt={0}>
              <StyledLabel title={__('Date')} />
              <EventDate
                timezoneConfiguration={timezoneConfiguration}
                event={event}
                clamp={1}
              />
            </ContentRow>
            {venue && (
              <ContentRow>
                <ClampedText clamp={2}>
                  <StyledLabel title={__('Venue')} />
                  {venue}
                </ClampedText>
              </ContentRow>
            )}
            {address && (
              <ContentRow>
                <StyledLabel title={__('Location')} />
                <ClampedText clamp={1}>{address}</ClampedText>
              </ContentRow>
            )}
          </Box>
          <Box flexDirection="column" flex="1">
            {webinarUrl && (
              <ContentRow mt={0} mb={2}>
                <StyledLabel title={__('Online event')} />
                <EventWebinarUrl webinar_url={webinarUrl} />
              </ContentRow>
            )}
            {displayAddToCalendar && (
              <ContentRow mt={0} mb={5}>
                <AddToCalendar
                  id={event.id}
                  timezone={timezone}
                  dropdownPosition="top"
                  eventType={event.type}
                />
              </ContentRow>
            )}
            {consolidatedAttendees?.length > 0 &&
              participantThresholdMet({
                threshold: participantDisplayThreshold,
                attendees: totalVisibleAttendees,
                responses: totalVisibleYesResponses,
              }) && (
                <ContentRow mt={0}>
                  <Attendees
                    totalAttendees={totalAttendees}
                    totalExternalAttendees={totalExternalAttendees}
                    attendees={consolidatedAttendees}
                    imgSize={40}
                    eventId={id}
                    maxAvatars={useIsMobileDevice ? 4 : 5}
                  />
                </ContentRow>
              )}
          </Box>
        </Box>
        <Box justifyContent="space-between" alignItems="center">
          <Box flex="0 0 auto" mr={3} display="block">
            <Button onClick={() => onFindMore(event)}>
              {__('Find out more')}
            </Button>
          </Box>
          {networkCategories?.length > 0 && (
            <PillsList
              pills={networkCategories}
              seeMoreLabel={__('See More')}
              seeLessLabel={s__('Tags|See less')}
            />
          )}
        </Box>
      </StyledCard>
    </Wrapper>
  );
};

export default EventsMapCard;

// COMPONENTS
const Label = ({ title, className }) => (
  <Box font="body-semibold-asphalt" className={className}>
    {s__('%{text}:').i__({ text: title })}
  </Box>
);

const ClampedText = ({ children, clamp, title, className }) => (
  <Dotdotdot clamp={clamp} useNativeClamp className={className}>
    <span title={title ?? children}>{children}</span>
  </Dotdotdot>
);

// STYLED COMPONENTS
const ContentRow = styled(Box).attrs(({ mt }) => ({
  mt: mt ?? 2,
}))`
  line-height: 20px;
`;

const StyledLabel = styled(Label)`
  display: inline-block;
  line-height: 20px;
  margin-right: ${({ theme }) => theme.space[1]}px;
  white-space: nowrap;
`;

const StyledTwoDigitsCalendar = styled(TwoDigitsCalendar)`
  box-shadow: 0 1px 6px 0 rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
  margin-top: 2px;
  margin-bottom: 2px;
`;

const EventTitle = styled(Box).attrs(() => ({
  ml: 3,
  mr: 3,
  font: { _: 'h4-semibold', l: 'h3-semibold' },
  flex: 1,
}))`
  cursor: pointer;
  text-decoration: none;
  ${linkHoverEffect}
`;

const StyledCard = styled(Card)`
  position: relative;
`;

/* This allows the box-shadow of the linkEffet to show up properly (it's cropped otherwise because of the overflow: hidden of
the Dotdotdot container) */
const ClampedLinkWorkaround = styled(ClampedText)`
  padding-bottom: 2px;
  margin-bottom: -2px;
`;

const Wrapper = styled(Box).attrs(() => ({
  position: 'absolute',
  bottom: 0,
  ml: 4,
  mr: 8,
  mb: 6,
  width: 'calc(100% - 32px)',
}))`
  // Must go hover the map
  z-index: ${zIndices.pinned};
  flex-direction: column;
`;
